(() => {
  'use strict';

  if (customElements.get('senix-kit-builder')) return;

  class SenixKitBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-senix-kit-config]')?.textContent || '{}');
      } catch (error) {
        this.showFatalConfigurationError('Kit configuration could not be read.');
        return;
      }

      this.options = Array.isArray(this.config.options) ? this.config.options : [];
      this.rules = Array.isArray(this.config.rules) ? this.config.rules : [];
      this.activeOptionIndex = 0;
      this.previewVariants = new Map();
      this.selectedVariants = new Map();
      this.skippedOptions = new Set();
      this.busy = false;

      if (!this.validateConfig()) {
        this.showFatalConfigurationError('Kit configuration incomplete.');
        return;
      }

      this.options.forEach((option) => {
        const preview = option.variants.find((variant) => String(variant.id) === String(option.defaultPreviewVariantId)) || option.variants[0];
        if (preview) this.previewVariants.set(option.key, preview);
      });

      this.cacheElements();
      this.bindEvents();
      this.moveNoindexToHead();
      this.render();
      this.track('senix_kit_view');
    }

    validateConfig() {
      if (!this.config.active || !this.config.handle || this.options.length === 0) return false;
      const keys = new Set();
      return this.options.every((option) => {
        if (!option?.key || keys.has(option.key) || !Array.isArray(option.variants) || option.variants.length === 0) return false;
        keys.add(option.key);
        option.variants = option.variants.filter((variant) => variant && variant.id && Number.isFinite(Number(variant.price)));
        return option.variants.length > 0;
      });
    }

    cacheElements(root = this) {
      this.host = root;
      this.ui = {
        steps: root.querySelector('[data-senix-kit-steps]'),
        mainImage: root.querySelector('[data-senix-kit-main-image]'),
        thumbnails: root.querySelector('[data-senix-kit-thumbnails]'),
        optionLabel: root.querySelector('[data-senix-kit-option-label]'),
        title: root.querySelector('[data-senix-kit-product-title]'),
        sku: root.querySelector('[data-senix-kit-sku]'),
        stock: root.querySelector('[data-senix-kit-stock]'),
        price: root.querySelector('[data-senix-kit-price]'),
        description: root.querySelector('[data-senix-kit-description]'),
        sellingPoints: root.querySelector('[data-senix-kit-selling-points]'),
        specifications: root.querySelector('[data-senix-kit-specifications]'),
        warranty: root.querySelector('[data-senix-kit-warranty]'),
        choiceTitle: root.querySelector('[data-senix-kit-choice-title]'),
        choices: root.querySelector('[data-senix-kit-choices]'),
        select: root.querySelector('[data-senix-kit-select]'),
        skip: root.querySelector('[data-senix-kit-skip]'),
        summaryLines: root.querySelector('[data-senix-kit-summary-lines]'),
        regularTotal: root.querySelector('[data-senix-kit-regular-total]'),
        savings: root.querySelector('[data-senix-kit-savings]'),
        finalTotal: root.querySelector('[data-senix-kit-final-total]'),
        completionNote: root.querySelector('[data-senix-kit-completion-note]'),
        add: root.querySelector('[data-senix-kit-add]'),
        buyNow: root.querySelector('[data-senix-kit-buy-now]'),
        error: root.querySelector('[data-senix-kit-error]'),
        status: root.querySelector('[data-senix-kit-status]'),
        summaryToggle: root.querySelector('[data-senix-kit-summary-toggle]'),
        summaryBody: root.querySelector('[data-senix-kit-summary-body]')
      };
    }

    bindEvents() {
      const sectionId = this.dataset.sectionId;
      document.addEventListener('click', (event) => {
        const host = event.target.closest('senix-kit-builder');
        if (!host || host.dataset.sectionId !== sectionId) return;
        this.cacheElements(host);
        const target = event.target.closest('button');
        if (!target) return;
        if (target.matches('[data-senix-kit-select]')) return this.confirmPreview();
        if (target.matches('[data-senix-kit-skip]')) return this.skipCurrentOption();
        if (target.matches('[data-senix-kit-add]')) return this.addKit(false);
        if (target.matches('[data-senix-kit-buy-now]')) return this.addKit(true);
        if (target.matches('[data-senix-kit-summary-toggle]')) return this.toggleSummary();
        if (target.dataset.senixKitStepIndex !== undefined) return this.editOption(Number(target.dataset.senixKitStepIndex));
        if (target.dataset.senixKitSummaryIndex !== undefined) return this.editOption(Number(target.dataset.senixKitSummaryIndex));
        if (target.dataset.senixKitVariantId) {
          const option = this.options.find((value) => value.key === target.dataset.senixKitOptionKey);
          const variant = option?.variants.find((value) => String(value.id) === target.dataset.senixKitVariantId);
          if (option && variant) return this.previewVariant(option.key, variant);
        }
        if (target.dataset.senixKitImageIndex !== undefined) return this.showGalleryImage(Number(target.dataset.senixKitImageIndex));
      });
    }

    moveNoindexToHead() {
      const meta = document.querySelector('[data-senix-kit-noindex]');
      if (meta && !document.head.querySelector('meta[name="robots"][data-senix-kit-noindex]')) document.head.append(meta);
    }

    render() {
      this.clearError();
      this.renderStepper();
      this.renderCurrentOption();
      this.renderSummary();
    }

    renderStepper() {
      this.ui.steps.replaceChildren(...this.options.map((option, index) => {
        const selected = this.selectedVariants.get(option.key);
        const active = index === this.activeOptionIndex;
        const item = document.createElement('li');
        item.className = `senix-kit__step${active ? ' is-active' : ''}${selected ? ' is-complete' : ''}`;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'senix-kit__step-button';
        button.setAttribute('aria-current', active ? 'step' : 'false');
        button.setAttribute('aria-label', `${selected ? 'Edit' : 'Go to'} step ${index + 1}: ${option.shortLabel}`);
        button.dataset.senixKitStepIndex = String(index);

        const marker = document.createElement('span');
        marker.className = 'senix-kit__step-marker';
        marker.textContent = selected ? '✓' : String(index + 1);
        marker.setAttribute('aria-hidden', 'true');

        const copy = document.createElement('span');
        copy.className = 'senix-kit__step-copy';
        const label = document.createElement('strong');
        label.textContent = option.shortLabel;
        const value = document.createElement('small');
        value.textContent = selected?.sku || (active ? 'Choosing now' : 'Not selected');
        copy.append(label, value);
        button.append(marker, copy);
        item.append(button);
        return item;
      }));
    }

    renderCurrentOption() {
      const option = this.options[this.activeOptionIndex];
      const variant = this.previewVariants.get(option.key) || option.variants[0];
      if (!variant) return;

      this.ui.optionLabel.textContent = option.badge || `Step ${this.activeOptionIndex + 1} of ${this.options.length}`;
      this.ui.title.textContent = variant.title;
      this.ui.sku.textContent = `SKU: ${variant.sku || '—'}`;
      this.ui.stock.textContent = variant.available ? 'In stock' : 'Out of stock';
      this.ui.stock.classList.toggle('is-unavailable', !variant.available);
      this.renderPrice(this.ui.price, variant);
      this.ui.description.textContent = variant.description || '';
      this.ui.choiceTitle.textContent = option.name;

      this.renderGallery(variant);
      this.renderDetails(variant);
      this.renderChoices(option, variant);

      this.ui.select.disabled = !variant.available || this.busy;
      this.ui.select.textContent = variant.available ? 'Select this product' : 'Out of stock';
      this.ui.skip.hidden = option.required;
    }

    renderGallery(variant) {
      const images = Array.isArray(variant.images) && variant.images.length
        ? variant.images
        : (variant.featuredImage?.url ? [variant.featuredImage] : []);
      const first = images[0];

      if (first?.url) {
        this.ui.mainImage.src = first.url;
        this.ui.mainImage.alt = first.alt || variant.title;
        this.ui.mainImage.width = Number(first.width) || 900;
        this.ui.mainImage.height = Number(first.height) || 900;
        this.ui.mainImage.hidden = false;
      } else {
        this.ui.mainImage.removeAttribute('src');
        this.ui.mainImage.alt = '';
        this.ui.mainImage.hidden = true;
      }

      this.ui.thumbnails.replaceChildren(...images.map((image, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `senix-kit__thumbnail${index === 0 ? ' is-active' : ''}`;
        button.setAttribute('aria-label', `View image ${index + 1} of ${variant.title}`);
        button.dataset.senixKitImageIndex = String(index);
        const img = document.createElement('img');
        img.src = image.thumb || image.url;
        img.alt = '';
        img.width = 76;
        img.height = 76;
        img.loading = index > 3 ? 'lazy' : 'eager';
        button.append(img);
        return button;
      }));
    }

    renderDetails(variant) {
      const points = Array.isArray(variant.sellingPoints) ? variant.sellingPoints.filter(Boolean) : [];
      this.ui.sellingPoints.replaceChildren(...points.map((point) => {
        const item = document.createElement('li');
        item.textContent = typeof point === 'string' ? point : point.text;
        return item;
      }));
      this.ui.sellingPoints.hidden = points.length === 0;

      const specifications = Array.isArray(variant.specifications) ? variant.specifications : [];
      const specNodes = [];
      specifications.forEach((specification) => {
        if (!specification?.name || !specification?.value) return;
        const term = document.createElement('dt');
        term.textContent = specification.name;
        const definition = document.createElement('dd');
        definition.textContent = specification.value;
        specNodes.push(term, definition);
      });
      this.ui.specifications.replaceChildren(...specNodes);
      this.ui.specifications.hidden = specNodes.length === 0;

      this.ui.warranty.hidden = !variant.warranty;
      this.ui.warranty.textContent = variant.warranty ? `Warranty: ${variant.warranty}` : '';
    }

    renderChoices(option, preview) {
      this.ui.choices.replaceChildren(...option.variants.map((variant) => {
        const selected = this.selectedVariants.get(option.key);
        const isPreview = String(preview.id) === String(variant.id);
        const isConfirmed = String(selected?.id) === String(variant.id);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `senix-kit__choice${isPreview ? ' is-preview' : ''}${isConfirmed ? ' is-confirmed' : ''}`;
        button.disabled = !variant.available;
        button.setAttribute('aria-pressed', isPreview ? 'true' : 'false');
        button.setAttribute('aria-label', `${isPreview ? 'Previewing' : 'Preview'} ${variant.title}, ${this.money(variant.price)}${variant.available ? '' : ', out of stock'}`);
        button.dataset.senixKitOptionKey = option.key;
        button.dataset.senixKitVariantId = String(variant.id);

        const image = document.createElement('img');
        image.src = variant.featuredImage?.url || '';
        image.alt = '';
        image.width = 84;
        image.height = 84;
        image.loading = 'lazy';

        const copy = document.createElement('span');
        copy.className = 'senix-kit__choice-copy';
        const title = document.createElement('strong');
        title.textContent = variant.title;
        const sku = document.createElement('small');
        sku.textContent = variant.sku || 'SKU unavailable';
        const price = document.createElement('span');
        price.textContent = variant.available ? this.money(variant.price) : 'Out of stock';
        copy.append(title, sku, price);

        const state = document.createElement('span');
        state.className = 'senix-kit__choice-state';
        state.textContent = isConfirmed ? 'Selected' : (isPreview ? 'Previewing' : 'Preview');
        button.append(image, copy, state);
        return button;
      }));
    }

    renderSummary() {
      this.ui.summaryLines.replaceChildren(...this.options.map((option, index) => {
        const variant = this.selectedVariants.get(option.key);
        const item = document.createElement('li');
        item.className = `senix-kit__summary-line${variant ? ' is-selected' : ''}`;
        const copy = document.createElement('span');
        const label = document.createElement('strong');
        label.textContent = option.shortLabel;
        const value = document.createElement('small');
        value.textContent = variant ? `${variant.sku} · ${this.money(variant.price)}` : (this.skippedOptions.has(option.key) ? 'Skipped' : 'Select a product');
        copy.append(label, value);
        const edit = document.createElement('button');
        edit.type = 'button';
        edit.textContent = variant ? 'Edit' : 'Choose';
        edit.dataset.senixKitSummaryIndex = String(index);
        item.append(copy, edit);
        return item;
      }));

      const pricing = this.calculatePricing();
      const ready = this.isComplete();
      this.ui.regularTotal.textContent = this.money(pricing.regularTotal);
      this.ui.savings.textContent = pricing.savings ? `−${this.money(pricing.savings)} (${this.formatPercent(pricing.discount)} off)` : this.money(0);
      this.ui.finalTotal.textContent = this.money(pricing.finalTotal);
      this.ui.completionNote.hidden = ready;
      this.ui.add.disabled = !ready || this.busy;
      this.ui.buyNow.disabled = !ready || this.busy;
    }

    previewVariant(optionKey, variant) {
      if (!variant.available || this.busy) return;
      this.previewVariants.set(optionKey, variant);
      this.renderCurrentOption();
      this.track('senix_kit_product_preview', { option_key: optionKey, variant_id: String(variant.id), sku: variant.sku });
    }

    showGalleryImage(index) {
      const option = this.options[this.activeOptionIndex];
      const variant = this.previewVariants.get(option.key) || option.variants[0];
      const images = Array.isArray(variant.images) && variant.images.length
        ? variant.images
        : (variant.featuredImage?.url ? [variant.featuredImage] : []);
      const image = images[index];
      if (!image?.url) return;
      this.ui.mainImage.src = image.url;
      this.ui.mainImage.alt = image.alt || variant.title;
      this.ui.thumbnails.querySelectorAll('.is-active').forEach((node) => node.classList.remove('is-active'));
      this.ui.thumbnails.querySelector(`[data-senix-kit-image-index="${index}"]`)?.classList.add('is-active');
    }

    confirmPreview() {
      const option = this.options[this.activeOptionIndex];
      const variant = this.previewVariants.get(option.key);
      if (!variant?.available) {
        this.showError('This item is no longer available. Please choose another option.');
        return;
      }
      this.selectedVariants.set(option.key, variant);
      this.skippedOptions.delete(option.key);
      this.track('senix_kit_product_select', { option_key: option.key, variant_id: String(variant.id), sku: variant.sku });
      this.advanceFrom(this.activeOptionIndex);
      this.render();
      if (this.isComplete()) this.track('senix_kit_complete', this.analyticsPricing());
    }

    skipCurrentOption() {
      const option = this.options[this.activeOptionIndex];
      if (option.required) return;
      this.selectedVariants.delete(option.key);
      this.skippedOptions.add(option.key);
      this.advanceFrom(this.activeOptionIndex);
      this.render();
    }

    advanceFrom(index) {
      for (let offset = 1; offset <= this.options.length; offset += 1) {
        const nextIndex = (index + offset) % this.options.length;
        const next = this.options[nextIndex];
        if (!this.selectedVariants.has(next.key) && !this.skippedOptions.has(next.key)) {
          this.activeOptionIndex = nextIndex;
          return;
        }
      }
      this.activeOptionIndex = Math.min(index + 1, this.options.length - 1);
    }

    editOption(index) {
      if (this.busy || index < 0 || index >= this.options.length) return;
      this.activeOptionIndex = index;
      const option = this.options[index];
      const selected = this.selectedVariants.get(option.key);
      if (selected) this.previewVariants.set(option.key, selected);
      this.render();
      this.track('senix_kit_option_edit', { option_key: option.key });
      this.host?.querySelector('[data-senix-kit-product]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    isComplete() {
      const required = this.options.filter((option) => option.required);
      return required.filter((option) => this.selectedVariants.has(option.key)).length === required.length;
    }

    calculatePricing() {
      const selected = [...this.selectedVariants.values()];
      const variantIds = selected.map((variant) => String(variant.id));
      const regularTotal = selected.reduce((total, variant) => total + Number(variant.price || 0), 0);
      let candidate = this.clampPercent(this.config.defaultDiscount);

      const matchingRules = this.rules.filter((rule) => rule.active !== false && this.ruleMatches(rule, variantIds));
      const matchingDiscountRules = matchingRules
        .filter((rule) => rule.type !== 'MAX_DISCOUNT_CAP')
        .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0) || Number(a.discount || 0) - Number(b.discount || 0));
      if (matchingDiscountRules.length) candidate = this.clampPercent(matchingDiscountRules[0].discount);

      const caps = matchingRules
        .filter((rule) => rule.cap !== null && rule.cap !== undefined && rule.cap !== '')
        .map((rule) => this.clampPercent(rule.cap));
      if (this.config.maximumDiscount !== null && this.config.maximumDiscount !== undefined && this.config.maximumDiscount !== '') caps.push(this.clampPercent(this.config.maximumDiscount));
      const globalCap = this.validPercent(this.config.globalMaxDiscount) ? Number(this.config.globalMaxDiscount) : 0;
      caps.push(globalCap);
      const discount = caps.length ? Math.min(candidate, ...caps) : candidate;
      const savings = Math.round(regularTotal * discount / 100);
      return { regularTotal, savings, finalTotal: regularTotal - savings, discount };
    }

    ruleMatches(rule, selectedIds) {
      const conditions = (rule.variantIds || []).map(String);
      if (!conditions.length) return false;
      const selectedSet = new Set(selectedIds);
      const conditionSet = new Set(conditions);
      switch (rule.type) {
        case 'CONTAINS_ALL':
          return conditions.every((id) => selectedSet.has(id));
        case 'EXACT_COMBINATION':
          return selectedSet.size === conditionSet.size && conditions.every((id) => selectedSet.has(id));
        case 'MAX_DISCOUNT_CAP':
        case 'CONTAINS_ANY':
        default:
          return conditions.some((id) => selectedSet.has(id));
      }
    }

    async addKit(buyNow) {
      if (!this.isComplete() || this.busy) return;
      this.setBusy(true, buyNow ? 'Preparing checkout…' : 'Adding your kit…');
      const instance = this.createInstanceId();
      const items = this.options
        .map((option) => ({ option, variant: this.selectedVariants.get(option.key) }))
        .filter(({ variant }) => variant)
        .map(({ option, variant }) => ({
          id: variant.id,
          quantity: 1,
          properties: {
            _senix_custom_kit: 'true',
            _senix_kit_handle: this.config.handle,
            _senix_kit_instance: instance,
            _senix_kit_option: option.key,
            _senix_kit_name: this.config.name
          }
        }));

      let errorMessage = '';
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 422) {
            const preview = this.previewVariants.get(this.options[this.activeOptionIndex].key);
            if (preview) preview.available = false;
            throw new Error('This item is no longer available. Please choose another option.');
          }
          throw new Error(result.description || result.message || 'Add to cart failed. Please try again.');
        }

        this.track(buyNow ? 'senix_kit_buy_now' : 'senix_kit_add_to_cart', { ...this.analyticsPricing(), kit_instance: instance });
        if (buyNow) {
          window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
          return;
        }
        await this.refreshExistingCart();
        this.ui.status.textContent = 'Your kit was added to the cart.';
      } catch (error) {
        errorMessage = error?.message || 'Add to cart failed. Please try again.';
      } finally {
        this.setBusy(false);
        this.render();
        if (errorMessage) this.showError(errorMessage);
      }
    }

    async refreshExistingCart() {
      const root = window.Shopify?.routes?.root || '/';
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });

      const drawerWrapper = document.querySelector('#halo-cart-sidebar .previewCart-wrapper');
      if (drawerWrapper) {
        const drawerResponse = await fetch(`${root}cart?view=ajax_side_cart`, { headers: { Accept: 'text/html' } });
        if (!drawerResponse.ok) throw new Error('Your kit was added, but the cart drawer could not refresh.');
        drawerWrapper.innerHTML = await drawerResponse.text();
        document.body.classList.add('cart-sidebar-show');
        document.querySelector('.background-overlay')?.classList.remove('hold');
        return;
      }

      if (window.halo && typeof window.halo.updateSidebarCart === 'function') {
        document.body.classList.add('cart-sidebar-show');
        window.halo.updateSidebarCart(cart);
        return;
      }
      window.location.assign(`${root}cart`);
    }

    setBusy(busy, message = '') {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      if (this.ui?.status) this.ui.status.textContent = message;
      if (this.ui?.select) this.ui.select.disabled = busy || !this.previewVariants.get(this.options[this.activeOptionIndex]?.key)?.available;
      if (this.ui?.add) this.ui.add.disabled = busy || !this.isComplete();
      if (this.ui?.buyNow) this.ui.buyNow.disabled = busy || !this.isComplete();
    }

    toggleSummary() {
      const expanded = this.ui.summaryToggle.getAttribute('aria-expanded') === 'true';
      this.ui.summaryToggle.setAttribute('aria-expanded', String(!expanded));
      this.ui.summaryToggle.querySelector('[aria-hidden]')?.replaceChildren(document.createTextNode(expanded ? '+' : '−'));
      this.ui.summaryBody.hidden = expanded;
    }

    renderPrice(container, variant) {
      container.replaceChildren();
      const current = document.createElement('strong');
      current.textContent = this.money(variant.price);
      container.append(current);
      if (variant.compareAtPrice && Number(variant.compareAtPrice) > Number(variant.price)) {
        const compare = document.createElement('s');
        compare.textContent = this.money(variant.compareAtPrice);
        container.append(compare);
      }
    }

    money(cents) {
      return new Intl.NumberFormat(this.config.locale || 'en-US', {
        style: 'currency',
        currency: this.config.currency || 'USD'
      }).format(Number(cents || 0) / 100);
    }

    formatPercent(value) {
      return `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    }

    clampPercent(value) {
      const number = Number(value);
      return Number.isFinite(number) ? Math.min(100, Math.max(0, number)) : 0;
    }

    validPercent(value) {
      return value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 100;
    }

    createInstanceId() {
      const random = globalThis.crypto?.randomUUID?.().replaceAll('-', '').slice(0, 12)
        || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      return `kit_${String(this.config.handle).replace(/[^a-z0-9_-]/gi, '').slice(0, 24)}_${random}`;
    }

    analyticsPricing() {
      const pricing = this.calculatePricing();
      return {
        discount_percentage: pricing.discount,
        kit_regular_total: pricing.regularTotal / 100,
        kit_final_total: pricing.finalTotal / 100
      };
    }

    track(event, details = {}) {
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({ event, kit_handle: this.config.handle, ...details });
    }

    showError(message) {
      if (!this.ui?.error) return;
      this.ui.error.textContent = message;
      this.ui.error.hidden = false;
    }

    clearError() {
      if (!this.ui?.error) return;
      this.ui.error.textContent = '';
      this.ui.error.hidden = true;
    }

    showFatalConfigurationError(message) {
      const shell = this.querySelector('.senix-kit__shell') || this;
      const error = document.createElement('div');
      error.className = 'senix-kit__configuration-message';
      error.setAttribute('role', 'status');
      error.textContent = message;
      shell.replaceChildren(error);
    }
  }

  customElements.define('senix-kit-builder', SenixKitBuilder);
})();
